import Checkbox from '@/components/Checkbox/Checkbox';
import { VideoBadge } from '@/pages/home/components/Common';
import { hasError, isDraft, isGenerating, isPlayable } from '@/pages/home/utils';
import { PlayModal } from '@/pages/video-library/components/Modals/PlayModal';
import { VideoOptionsList } from '@/pages/video-library/components/VideoOptionsList';
import { useAnalytics } from '@/services/analytics';
import { useSelectedVideos } from '@/state/video-library/videoLibrary';
import { formatDate } from '@/utils/date';
import { MixPanelEvent } from '@/utils/types';
import { LibraryItem } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { CheckboxContainer, CoreVideoTile } from './CoreVideoTile';
import { useUpdateVideoName } from './useUpdateVideoName';
import { VideoTileInfo } from './VideoTileInfo';

const TileWrapper = styled.div<{
    isItemSelected: boolean;
    isCardSelectable: boolean;
    selectLimitReached: boolean;
}>`
    ${({ theme, isItemSelected }) =>
        isItemSelected &&
        `
        > div > div:first-of-type {
            border: 2px solid ${theme.colors.secondary[900]};
        }
    `}

    ${({ selectLimitReached }) =>
        selectLimitReached &&
        `
        > div > div:first-of-type {
            cursor: not-allowed;
        }
    `}

    ${({ isCardSelectable }) =>
        !isCardSelectable &&
        `
        :hover .CheckboxContainer {
            opacity: 1;
        }
    `}

    ${({ isItemSelected, isCardSelectable, selectLimitReached }) =>
        (isItemSelected || (isCardSelectable && !selectLimitReached)) &&
        `
            .CheckboxContainer {
                opacity: 1;
            }
        `}
`;

interface Props {
    item: LibraryItem;
    origin: string;
}

const getVideoId = ({
    isVideoWatched,
    presenterVersion,
    presenterVersions,
}: {
    isVideoWatched: boolean;
    presenterVersion?: string;
    presenterVersions?: string[];
}) => {
    if (!isVideoWatched) {
        return '';
    }

    if (presenterVersions?.length) {
        const isSamePresenterVersion = presenterVersions.every(presenter => presenter === presenterVersions[0]);
        if (!isSamePresenterVersion) {
            return '';
        }
        return `${presenterVersions[0] === 'v2' ? 'clip_v2' : presenterVersions[0]}_viewed`;
    }

    return `${presenterVersion === 'v2' ? 'clip_v2' : presenterVersion}_viewed`;
};

export const DIDVideoTile = ({ item, origin }: Props) => {
    const [keepHover, setKeepHover] = useState(false);
    const [isVideoWatched, setIsVideoWatched] = useState(false);
    const [videoName, setVideoName] = useState(item.name);
    const { onSelect, isSelected, selectMode, bulkActionsEnabled, limitReached: selectLimitReached } = useSelectedVideos();
    const mixpanel = useAnalytics();

    const { updateName } = useUpdateVideoName({
        item,
        onError: originalName => {
            setVideoName(originalName);
        },
    });

    const id = getVideoId({ isVideoWatched, presenterVersion: item.presenter_version, presenterVersions: item.presenter_versions });
    const formattedDate = formatDate(item.created_at);
    const isLoading = isGenerating(item);
    const isError = hasError(item);
    const stuckOnLoading = isLoading && Date.now() - new Date(item.started_at ?? item.created_at).getTime() > 1000 * 60 * 15;
    const isDone = !isLoading && !isError;
    const isDraftProject = isDraft(item);

    const isCardSelectable = selectMode && !isLoading;
    const isItemSelected = isSelected(item.id);
    const disableSelect = selectLimitReached && isCardSelectable && !isItemSelected;

    const onVideoPlay = () => {
        setIsVideoWatched(true);
        mixpanel?.track('video-preview', {
            event: MixPanelEvent.CLICK,
            from: origin,
            videoId: item.id,
            editorType: 'editor',
        });
    };

    const handleBlur = async (value: string) => {
        if (!value) {
            setVideoName(item.name);
        } else if (value !== item?.name) {
            mixpanel?.track('project-name', { event: 'click', value, from: origin });
            await updateName(value);
        }
    };

    useEffect(() => {
        if (item.name !== videoName) {
            setVideoName(item.name);
        }
    }, [item.name]);

    const handleSelect = (e: React.MouseEvent<Element>) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(item);
    };

    const controls = (
        <>
            {isDone && !isCardSelectable && (
                <PlayModal
                    onPlay={onVideoPlay}
                    video={item}
                    isError={isError}
                    isPlayable={isPlayable(item)}
                    showOpenButton={!stuckOnLoading}
                    origin={origin}
                />
            )}
            {!isItemSelected && (
                <VideoOptionsList video={item} onOpen={() => setKeepHover(true)} onClose={() => setKeepHover(false)} origin={origin} />
            )}
        </>
    );

    const checkbox =
        bulkActionsEnabled && !isLoading ? (
            <CheckboxContainer className="CheckboxContainer">
                <Checkbox checked={isItemSelected} onClick={handleSelect} disabled={disableSelect} />
            </CheckboxContainer>
        ) : null;

    const badge = isDraftProject ? (
        <VideoBadge testId={`video-tile-badge-draft-${item.name}`} isDraft />
    ) : (
        <VideoBadge testId={`video-tile-badge-duration-${item.name}`} duration={item.duration} isGenerating={isGenerating(item)} />
    );

    const tileInfo = (
        <VideoTileInfo
            item={item}
            videoName={videoName}
            setVideoName={setVideoName}
            handleBlur={handleBlur}
            formattedDate={formattedDate}
            isError={isError}
        />
    );

    return (
        <TileWrapper
            isItemSelected={isItemSelected}
            isCardSelectable={isCardSelectable}
            selectLimitReached={!isLoading && selectLimitReached}>
            <CoreVideoTile
                item={item}
                id={id}
                hovered={keepHover}
                isDone={isDone}
                isError={isError}
                isLoading={isLoading}
                badge={badge}
                controls={controls}
                tileInfo={tileInfo}
                checkbox={checkbox}
                onClick={isCardSelectable ? handleSelect : undefined}
                disableHover={isCardSelectable}
                showSelectBorder={isItemSelected}
                selectCursor={disableSelect ? 'not-allowed' : undefined}
            />
        </TileWrapper>
    );
};
